import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ArticleSummary, Topic } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Reveal } from '@/components/motion/reveal'
import { HeroHeading } from '@/components/site/hero-heading'
import { JournalCard } from '@/components/site/journal-card'
import { JournalFeatured } from '@/components/landing/journal/journal-featured'
import { JournalTopics } from '@/components/landing/journal/journal-topics'
import { getArticles, getTopics } from '@/lib/content'

interface JournalPageProps {
    articles: ArticleSummary[]
    topics: Topic[]
}

const JournalPage: FC<JournalPageProps> = ({ articles, topics }): ReactNode => {
    const featured = articles.find((article) => article.featured) ?? null

    return (
        <Layout>
            <Seo
                title="Journal"
                description="Notes from Zaki Hatta on engineering, maps and GIS, frontend development and the move from student to software engineer."
            />

            <section
                id='hero'
                className='relative z-[2] flex h-[50vh] w-full items-end justify-center overflow-clip bg-background px-4 pb-6 pt-hero lg:px-6'
            >
                <div className='flex flex-1 flex-col items-start'>
                    <HeroHeading first="Notes" connector="from" second="The desk" align='start' />
                </div>
            </section>

            {featured && <JournalFeatured article={featured} />}

            <section
                id='journal'
                className='relative z-[2] flex w-full overflow-clip bg-background px-4 pb-section-lg pt-section-sm lg:px-6'
            >
                {articles.length > 0 ? (
                    <ul className='grid w-full flex-1 auto-rows-[minmax(0,1fr)] grid-cols-1 gap-12 md:grid-cols-3 md:gap-x-10 md:gap-y-16 lg:gap-x-16 lg:gap-y-20'>
                        {articles.map((article, index) => (
                            <li key={article.id} className='flex w-full'>
                                <Reveal className='w-full' delay={(index % 3) * 0.08}>
                                    <JournalCard article={article} />
                                </Reveal>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className='t-italic text-left text-muted-foreground'>
                        New writing is on its way.
                    </p>
                )}
            </section>

            <JournalTopics topics={topics} articles={articles} />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<JournalPageProps> = async () => {
    try {
        const [articles, topics] = await Promise.all([getArticles(), getTopics()])

        return { props: { articles, topics }, revalidate: 60 }
    }
    catch (error) {
        console.error('Journal content could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { articles: [], topics: [] }, revalidate: 10 }
    }
}

export default JournalPage
